import { useCallback, useEffect, useMemo } from 'react'
import { useQueries, type UseQueryResult } from '@tanstack/react-query'
import { fetchChatMessages, normalizeApiMessages } from '@renderer/chat/hooks/chat-messages'
import { useAuth } from '@renderer/chat/providers'
import { useReadAllChatMessages } from '@renderer/chat/store/messages'
import type { ChatMessage } from '@renderer/chat/types'
import { useHistoryActivity } from '@renderer/overlay/Sidepanel/hooks/use-history-activity'
import { getActivityTitle } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import type { RecentActivity } from '@renderer/overlay/Sidepanel/types'

// Chats have no search index yet, so search covers only the most recent chats History loads. A chat's messages are
// searched only when the app holds them in memory or loads them here.
const RECENT_CHAT_LIMIT = 100
/** The newest chats whose messages are loaded for search when the app doesn't already hold them. */
const LOADED_CHAT_LIMIT = 20
const SNIPPET_LEAD = 40
const SNIPPET_LENGTH = 160

/** A passage holding the query, in the shape `HighlightedFragment` draws. Offsets are into the message text. */
export interface ChatSnippet {
  text: string
  fragmentRange: [number, number]
  matchRanges: Array<[number, number]>
}

export interface ChatMatch {
  item: RecentActivity
  /** The first message passage holding the query, or null when only the title matched. */
  snippet: ChatSnippet | null
}

interface RecentChatSearch {
  matches: Array<ChatMatch>
  isLoading: boolean
}

/** Matches the user's recent chats on their title and on what was said in them, ignoring case. */
export function useRecentChatSearch(query: string): RecentChatSearch {
  const recentChats = useRecentChats()
  const loadedChats = useLoadedChatMessages(recentChats.items)
  const readAllChatMessages = useReadAllChatMessages()

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) {
      return []
    }
    // Read, not subscribed, since the store changes on every streaming token. Memory has the newest messages, so it
    // wins unless it's empty.
    const inMemory = readAllChatMessages()
    const found: Array<ChatMatch> = []
    for (const item of recentChats.items) {
      const messages = inMemory[item.id]?.length ? inMemory[item.id] : (loadedChats.byChatId[item.id] ?? [])
      const match = matchChat(item, messages, needle)
      if (match) found.push(match)
    }
    return found
  }, [recentChats.items, loadedChats.byChatId, readAllChatMessages, query])

  // A miss isn't final until the chats and their messages are in, so a later match doesn't flash "no matches" first.
  const isStillLoading = recentChats.isLoadingMore || loadedChats.isLoading
  return { matches, isLoading: recentChats.isLoading || (matches.length === 0 && isStillLoading) }
}

function useRecentChats() {
  const { items, isLoading, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = useHistoryActivity(
    { type: 'chat' },
  )
  const isLoadingMore = hasNextPage && !isFetchNextPageError && items.length < RECENT_CHAT_LIMIT

  useEffect(() => {
    if (isLoadingMore && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [isLoadingMore, isFetchingNextPage, fetchNextPage])

  return { items, isLoading, isLoadingMore }
}

/** The messages of the newest chats the app doesn't hold in memory, loaded the way opening a chat loads them. */
function useLoadedChatMessages(chats: Array<RecentActivity>) {
  const { getAuthToken } = useAuth()
  const readAllChatMessages = useReadAllChatMessages()
  const chatIdsToLoad = useMemo(() => {
    const inMemory = readAllChatMessages()
    return chats
      .slice(0, LOADED_CHAT_LIMIT)
      .map((chat) => chat.id)
      .filter((id) => !inMemory[id]?.length)
  }, [chats, readAllChatMessages])

  const combineLoaded = useCallback(
    (results: Array<UseQueryResult<Array<ChatMessage>>>) => {
      const byChatId: Record<string, Array<ChatMessage>> = {}
      results.forEach((result, index) => {
        if (result.data) byChatId[chatIdsToLoad[index]] = result.data
      })
      return { byChatId, isLoading: results.some((result) => result.isLoading) }
    },
    [chatIdsToLoad],
  )

  return useQueries({
    queries: chatIdsToLoad.map((chatId) => ({
      queryKey: ['sidebar-search', 'chat-messages', chatId],
      queryFn: async () => normalizeApiMessages(await fetchChatMessages(chatId, getAuthToken), chatId),
      staleTime: 60_000,
    })),
    combine: combineLoaded,
  })
}

function matchChat(item: RecentActivity, messages: Array<ChatMessage>, needle: string): ChatMatch | null {
  if (getActivityTitle(item).toLowerCase().includes(needle)) {
    return { item, snippet: null }
  }
  for (const message of messages) {
    if (message.role === 'tool' || typeof message.content !== 'string') continue
    const snippet = snippetAround(message.content, needle)
    if (snippet) return { item, snippet }
  }
  return null
}

function snippetAround(text: string, needle: string): ChatSnippet | null {
  const start = text.toLowerCase().indexOf(needle)
  if (start === -1) {
    return null
  }
  const fragmentStart = Math.max(0, start - SNIPPET_LEAD)
  const fragmentEnd = Math.min(text.length, fragmentStart + SNIPPET_LENGTH)
  return {
    text: text.slice(fragmentStart, fragmentEnd),
    fragmentRange: [fragmentStart, fragmentEnd],
    matchRanges: [[start, start + needle.length]],
  }
}
